// File path: src/utils/coinglass.ts

import type {
  FundingRateDataPoint,
  OpenInterestDataPoint,
  WeightedDataPoint,
} from "@/types/coinglass";
import { AGGREGATION_LEVELS } from "@/constants/coinglass/coinglass";


// ============================================================================
// DATE CONVERSION UTILITIES
// ============================================================================

/**
 * Convert date integer (YYYYMMDD) to Unix timestamp in milliseconds
 * @param dateInt - Date in YYYYMMDD format (e.g., 20240315)
 * @returns Unix timestamp in milliseconds
 */
export function dateIntToTimestamp(dateInt: number): number {
  const dateStr = dateInt.toString();
  const year = parseInt(dateStr.substring(0, 4), 10);
  const month = parseInt(dateStr.substring(4, 6), 10) - 1; // JS months are 0-indexed
  const day = parseInt(dateStr.substring(6, 8), 10);
  return new Date(year, month, day).getTime();
}

/**
 * Capitalize first letter of exchange name for display
 */
export const capitalizeExchange = (exchange: string): string => {
  return exchange.charAt(0).toUpperCase() + exchange.slice(1);
};

/**
 * Format large numbers with appropriate suffix (B, M, K)
 * Dynamically adjusts based on value magnitude
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string with $ prefix and suffix
 */
export const formatLargeNumber = (value: number, decimals: number = 1): string => {
  const absValue = Math.abs(value);

  if (absValue >= 1e9) {
    // Billions
    return `$${(value / 1e9).toFixed(decimals)}B`;
  } else if (absValue >= 1e6) {
    // Millions
    return `$${(value / 1e6).toFixed(decimals)}M`;
  } else if (absValue >= 1e3) {
    // Thousands
    return `$${(value / 1e3).toFixed(decimals)}K`;
  } else {
    // Raw value
    return `$${value.toFixed(decimals)}`;
  }
};

/**
 * Format price values for y-axis display
 * Similar to formatLargeNumber but optimized for price ranges
 * @param value - The price to format
 * @returns Formatted string with $ prefix and suffix
 */
export const formatPrice = (value: number): string => {
  const absValue = Math.abs(value);

  if (absValue >= 1000) {
    // Show in K for prices >= 1000
    return `$${(value / 1000).toFixed(0)}K`;
  } else if (absValue >= 1) {
    // Show with minimal decimals for prices >= 1
    return `$${value.toFixed(0)}`;
  } else {
    // Show with more precision for small prices
    return `$${value.toFixed(2)}`;
  }
};

// ============================================================================
// DATA AGGREGATION
// ============================================================================

/**
 * Calculate aggregation level based on visible data point count
 * @param visiblePointCount - Number of visible data points
 * @returns Aggregation factor (1 = no aggregation)
 */
export function calculateAggregationLevel(visiblePointCount: number): number {
  if (visiblePointCount < 500) return AGGREGATION_LEVELS.NONE;
  if (visiblePointCount < 1000) return AGGREGATION_LEVELS.LOW;
  if (visiblePointCount < 2000) return AGGREGATION_LEVELS.MEDIUM;
  if (visiblePointCount < 5000) return AGGREGATION_LEVELS.HIGH;
  return AGGREGATION_LEVELS.VERY_HIGH;
}

// ============================================================================
// CHART DATA PREPARATION
// ============================================================================

/**
 * Aggregate data points for performance optimization
 * Groups consecutive points and averages their values
 *
 * IMPORTANT: Collects ALL exchanges from ALL data points in chunk,
 * not just from chunk[0], since different exchanges may appear on different dates
 */
export const aggregateData = (
  data: FundingRateDataPoint[],
  aggregation: number
): FundingRateDataPoint[] => {
  if (aggregation === 1) return data;

  const aggregated: FundingRateDataPoint[] = [];

  for (let i = 0; i < data.length; i += aggregation) {
    const chunk = data.slice(i, i + aggregation);
    if (chunk.length === 0) continue;

    const timestamp = chunk[0]?.timestamp ?? 0;

    // Average prices, treating null as 0
    const validPrices = chunk
      .filter((d) => d.price !== null)
      .map((d) => d.price as number);
    const avgPrice =
      validPrices.length > 0
        ? validPrices.reduce((sum, p) => sum + p, 0) / validPrices.length
        : null;

    // Collect ALL unique exchange names from ALL data points in this chunk
    const allExchangesSet = new Set<string>();
    chunk.forEach((d) => {
      Object.keys(d.exchanges).forEach((ex) => allExchangesSet.add(ex));
    });

    const exchanges: { [key: string]: number } = {};

    // For each exchange, only average data points where that exchange exists
    allExchangesSet.forEach((exchange) => {
      const validRates = chunk
        .filter((d) => exchange in d.exchanges)
        .map((d) => d.exchanges[exchange])
        .filter((rate): rate is number => typeof rate === "number");

      if (validRates.length > 0) {
        const avgRate =
          validRates.reduce((sum, rate) => sum + rate, 0) / validRates.length;
        exchanges[exchange] = avgRate;
      }
    });

    aggregated.push({ timestamp, price: avgPrice, exchanges });
  }

  return aggregated;
};

/**
 * Calculate optimal aggregation level based on zoom
 * Target: ~400 bars on screen for smooth rendering
 */
export const getAggregationLevel = (
  totalPoints: number,
  visiblePercentage: number
): number => {
  const visiblePoints = Math.ceil((totalPoints * visiblePercentage) / 100);
  const targetBars = 400;

  if (visiblePoints <= targetBars) return 1;

  const aggregationFactor = Math.ceil(visiblePoints / targetBars);

  // Daily aggregation levels
  if (aggregationFactor <= 1) return 1; // 1 day
  if (aggregationFactor <= 3) return 3; // 3 days
  if (aggregationFactor <= 6) return 6; // ~1 week
  if (aggregationFactor <= 12) return 12; // ~2 weeks
  return 24; // ~1 month
};

/**
 * Aggregate data points for performance optimization
 * Groups consecutive points and averages their values
 */
export const aggregateDataOpenInterest = (
  data: OpenInterestDataPoint[],
  aggregation: number
): OpenInterestDataPoint[] => {
  if (aggregation === 1) return data;

  const aggregated: OpenInterestDataPoint[] = [];

  for (let i = 0; i < data.length; i += aggregation) {
    const chunk = data.slice(i, i + aggregation);
    if (chunk.length === 0) continue;

    const timestamp = chunk[0]?.timestamp ?? 0;
    const avgPrice =
      chunk.reduce((sum, d) => sum + (d.price ?? 0), 0) / chunk.length;
    const avgOI =
      chunk.reduce((sum, d) => sum + d.openInterest, 0) / chunk.length;

    aggregated.push({
      timestamp,
      price: avgPrice,
      openInterest: avgOI,
    });
  }

  return aggregated;
};

/**
 * Calculate optimal aggregation level based on zoom
 * Target: ~180 points on screen for smooth rendering
 */
export const getAggregationLevel2 = (
  totalPoints: number,
  visiblePercentage: number
): number => {
  const visiblePoints = Math.ceil((totalPoints * visiblePercentage) / 100);
  const targetPoints = 180;

  if (visiblePoints <= targetPoints) return 1;

  const aggregationFactor = Math.ceil(visiblePoints / targetPoints);

  if (aggregationFactor <= 1) return 1;
  if (aggregationFactor <= 7) return 7;
  if (aggregationFactor <= 14) return 14;
  if (aggregationFactor <= 30) return 30;
  if (aggregationFactor <= 90) return 90;
  return 365;
};


/**
 * Aggregate data points for performance optimization
 * Groups consecutive points and averages their values
 */
export const aggregateDataWeightedFundingRate = (
  data: WeightedDataPoint[],
  aggregation: number
): WeightedDataPoint[] => {
  if (aggregation === 1) return data;

  const aggregated: WeightedDataPoint[] = [];

  for (let i = 0; i < data.length; i += aggregation) {
    const chunk = data.slice(i, i + aggregation);
    if (chunk.length === 0) continue;

    const timestamp = chunk[0]?.timestamp ?? 0;
    const validPrices = chunk.filter((d) => d.price !== null).map((d) => d.price as number);
    const avgPrice = validPrices.length > 0
      ? validPrices.reduce((sum, p) => sum + p, 0) / validPrices.length
      : null;
    const avgRate =
      chunk.reduce((sum, d) => sum + d.fundingRate, 0) / chunk.length;

    aggregated.push({
      timestamp,
      price: avgPrice,
      fundingRate: avgRate,
    });
  }

  return aggregated;
};
